'use client';

import { createContext, useContext, useCallback, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { Product } from '@/lib/shopify/types';

interface WishlistItem {
  id: string;
  handle: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  } | null;
  image: {
    url: string;
    altText: string | null;
  } | null;
  availableForSale: boolean;
  vendor?: string;
}

interface WishlistContextType {
  items: WishlistItem[];
  isLoading: boolean;
  addItem: (product: Product) => void;
  removeItem: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  toggleItem: (product: Product) => void;
  clearWishlist: () => void;
  itemCount: number;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

const WISHLIST_KEY = 'ok_tshirt_wishlist';
const WISHLIST_CHANGE_EVENT = 'ok_tshirt_wishlist_change';

function parseWishlistItems(value: string | null): WishlistItem[] {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error loading wishlist:', error);
    return [];
  }
}

function getWishlistSnapshot(): string {
  if (typeof window === 'undefined') return '[]';
  return window.localStorage.getItem(WISHLIST_KEY) || '[]';
}

function subscribeToWishlist(onStoreChange: () => void) {
  if (typeof window === 'undefined') return () => {};

  const handleChange = () => onStoreChange();
  window.addEventListener('storage', handleChange);
  window.addEventListener(WISHLIST_CHANGE_EVENT, handleChange);

  return () => {
    window.removeEventListener('storage', handleChange);
    window.removeEventListener(WISHLIST_CHANGE_EVENT, handleChange);
  };
}

function writeWishlistItems(items: WishlistItem[]) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event(WISHLIST_CHANGE_EVENT));
  } catch (error) {
    console.error('Error saving wishlist:', error);
  }
}

export function WishlistProvider({ children }: { children: ReactNode }) {
  const wishlistSnapshot = useSyncExternalStore(
    subscribeToWishlist,
    getWishlistSnapshot,
    () => '[]'
  );
  const items = useMemo(() => parseWishlistItems(wishlistSnapshot), [wishlistSnapshot]);

  const addItem = useCallback((product: Product) => {
    const currentItems = parseWishlistItems(getWishlistSnapshot());

    if (currentItems.some((item) => item.id === product.id)) return;

    const newItem: WishlistItem = {
      id: product.id,
      handle: product.handle,
      title: product.title,
      price: product.priceRange.minVariantPrice,
      compareAtPrice: product.variants[0]?.compareAtPrice || null,
      image: product.featuredImage,
      availableForSale: product.availableForSale,
      vendor: product.vendor,
    };

    writeWishlistItems([...currentItems, newItem]);
  }, []);

  const removeItem = useCallback((productId: string) => {
    const currentItems = parseWishlistItems(getWishlistSnapshot());
    writeWishlistItems(currentItems.filter((item) => item.id !== productId));
  }, []);

  const isInWishlist = useCallback(
    (productId: string) => {
      return items.some((item) => item.id === productId);
    },
    [items]
  );

  const toggleItem = useCallback(
    (product: Product) => {
      if (isInWishlist(product.id)) {
        removeItem(product.id);
      } else {
        addItem(product);
      }
    },
    [isInWishlist, removeItem, addItem]
  );

  const clearWishlist = useCallback(() => {
    writeWishlistItems([]);
  }, []);

  return (
    <WishlistContext.Provider
      value={{
        items,
        isLoading: false,
        addItem,
        removeItem,
        isInWishlist,
        toggleItem,
        clearWishlist,
        itemCount: items.length,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);

  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }

  return context;
}
